import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { ECOM, ECOMMERCE } from '../../constants/constant';
import WalmartWeekFilter from './WalmartWeekFilter';
import { saveWeekFilter } from '../../redux/actions/salesActions';
import {
	setViewByFilterAction,
	setBreakdownByFilterAction,
	setTimePeriodAction,
	setNewTimePeriodAction,
	resetFilters,
} from '../../redux/actions/tableActions';
import { getWeekFilterDateRange } from './util';
import analytics from '../../helper/analytics';
import tracker from '../../constants/tracker';
import ChannelFilter from './ChannelFilter';
import { useStyles } from './styles';
import './style.css';
import HierarchyFilter from './hierarchyFilter';
import CopyPasteUpcs from '../UpcCopyPaste';
import { resetValidateHierarchy } from '../../redux/actions/fullCompanyViewAction';
import { UserPortalContext } from '../../context/appcontext';
import { saveEventTrack } from '../../redux/actions/eventTrackAction';
import DataRefresh from './DataRefresh/index.js';
import { useDispatch } from 'react-redux';
import {
	STORE,
	MAIN_INSIGHTS_DASHBOARD,
	GUIDED_INSIGHTS_HOME,
	STORE_DRILL_DOWN_PAGE,
	CANNED_TIME_WINDOW_SELECTED,
	CHANGE_TIME_SELECTION,
	CANNED_TIME_WINDOW_SELECTION,
	TIME_SELECTION,
} from '../../constants/constant';
import { useLocation } from 'react-router-dom';

function Filter(props) {
	const {
		weekFilter,
		selectedFilter,
		setSelectedFilter,
		setWeekFilter,
		startWeek,
		endWeek,
		startDate,
		endDate,
		isItemDrilldown,
		tabName,
		isStoreDrilldown,
		setCurrentFilters,
	} = props;

	const location = useLocation();

	const dispatch = useDispatch();
	const classesName = useStyles(isStoreDrilldown);

	const portalUserContext = React.useContext(UserPortalContext);

	const durationType = {
		'-1': `CUSTOM`,
		0: `WTD`,
		1: `LW`,
		2: `L2W`,
		4: `L4W`,
		13: `L13W`,
		26: `L26W`,
		52: `L52W`,
	};

	// Sets default start & end date in Store(sales > selectedFilter)
	useEffect(() => {
		if (!isStoreDrilldown) {
			const localStorageFilters = JSON.parse(
				sessionStorage.getItem('weekfilters')
			);
			let localStartDate = '';
			let localEndDate = '';

			if (localStorageFilters) {
				localStartDate = localStorageFilters?.startDate || '';
				localEndDate = localStorageFilters?.endDate || '';
			}

			const { startDate, endDate } = getWeekFilterDateRange({
				weekFilter,
			});

			if (!isItemDrilldown || !isStoreDrilldown) {
				if (localStartDate !== '') {
					dispatch(
						saveWeekFilter({
							startDate: localStartDate,
							endDate: localEndDate,
						})
					);
				} else {
					dispatch(
						saveWeekFilter({
							startDate,
							endDate,
						})
					);
				}
			}

			let weekFilters;

			if (localStartDate) {
				weekFilters = {
					weekFilter,
					startWeek,
					endWeek,
					startDate: localStartDate,
					endDate: localEndDate,
				};
			} else {
				weekFilters = {
					weekFilter,
					startWeek,
					endWeek,
					startDate,
					endDate,
				};
			}

			sessionStorage.setItem('weekfilters', JSON.stringify(weekFilters));
		}
	}, []);

	useEffect(() => {
		return dispatch(resetValidateHierarchy());
	});

	const pageInfo = () => {
		if (window.location.toString().includes('item-details')) {
			return 'Item Details';
		} else if (window.location.toString().includes('guided-insights')) {
			return GUIDED_INSIGHTS_HOME;
		} else if (window.location.toString().includes('store-drilldown')) {
			return STORE_DRILL_DOWN_PAGE;
		} else {
			return MAIN_INSIGHTS_DASHBOARD;
		}
	};

	const getEventTrackingAttribute = () => {
		const text = pageInfo();
		return {
			string: `{"place": "${text}"}`,
		};
	};

	const handleWeekChange = (event) => {
		// The startDate & endDate in event.target will be undefined for the normal week selections,
		// but it has data only when the Custom selection is made
		// The value will be -1 for Custom week selection
		const {
			target: {
				value,
				startWeek,
				endWeek,
				startDate: startDateInput,
				endDate: endDateInput,
			},
		} = event;

		const weekFilter = Number(value);
		setWeekFilter(weekFilter);

		// Reset Time Period Comparison
		dispatch(setTimePeriodAction([]));
		dispatch(setNewTimePeriodAction([]));

		const { startDate, endDate } = getWeekFilterDateRange({
			startDateInput,
			endDateInput,
			weekFilter,
		});

		if (!isStoreDrilldown) {
			dispatch(
				saveWeekFilter({
					weekFilter,
					startWeek,
					endWeek,
					startDate,
					endDate,
				})
			);
		}

		const weekFilters = { weekFilter, startWeek, endWeek, startDate, endDate };
		const path = window.location.toString().includes('store-drilldown');
		const timeSelectionText = path
			? CANNED_TIME_WINDOW_SELECTED
			: CHANGE_TIME_SELECTION;
		const eventDescriptionText = path
			? CANNED_TIME_WINDOW_SELECTION
			: TIME_SELECTION;

		const request = {
			event: timeSelectionText,
			event_description: `${eventDescriptionText}${
				durationType[weekFilters.weekFilter]
			}`,
			attributes: getEventTrackingAttribute(),
		};
		dispatch(saveEventTrack({ request, portalUserContext }));

		if (!isStoreDrilldown) {
			sessionStorage.setItem('weekfilters', JSON.stringify(weekFilters));
		} else if (setCurrentFilters) {
			setCurrentFilters(weekFilters);
		}

		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: tracker.ACTION_ON.WEEK_SELECTION_DROPDOWN,
		});
		//Tracking Timeframe drop-down names clicked
		//We are tracking custom somewhere else
		if (durationType[weekFilters.weekFilter] === 'CUSTOM') {
			analytics.trackEventInfo({
				type: tracker.EVENTS.CLICK,
				actionOn: tracker.ACTION_ON.CUSTOMWEEK_APPLIED,
			});
		} else {
			analytics.trackEventInfo({
				type: tracker.EVENTS.CLICK,
				actionOn: durationType[weekFilters.weekFilter],
			});
		}
	};

	//Channel BreakDown filter
	const onFilterClick = function (e) {
		const selectedChannel =
			e?.target?.value === ECOMMERCE ? ECOM : e?.target?.value;
		setSelectedFilter(selectedChannel);
		dispatch(setViewByFilterAction('DEPARTMENT'));
		dispatch(setBreakdownByFilterAction(''));

		if (!isStoreDrilldown) {
			dispatch(
				saveWeekFilter({
					weekFilter,
					selectedFilter: selectedChannel,
					startWeek,
					endWeek,
					startDate,
					endDate,
				})
			);
		}

		dispatch(resetFilters());
		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: tracker.ACTION_ON.CHANNEL_BREAKDOWN_BUTTON,
		});
		const request = {
			event: 'Change Filter Selection',
			event_description: `Filter Selection: ${selectedChannel}`,
			attributes: MAIN_INSIGHTS_DASHBOARD,
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	};

	return (
		<>
			<Box className={classesName.container}>
				<Box className={classesName.menuRow}>
					<Box className={classesName.menuFilters}>
						<Box>
							<WalmartWeekFilter
								weekFilter={weekFilter}
								handleWeekChange={handleWeekChange}
								setWeekFilter={setWeekFilter}
								startWeek={startWeek}
								endWeek={endWeek}
								startDate={startDate}
								endDate={endDate}
								tabName={tabName}
								isStoreDrilldown={isStoreDrilldown}
							/>
						</Box>
						{!isItemDrilldown && !isStoreDrilldown && (
							<>
								<Box className={classesName.appFilter}>
									<ChannelFilter
										onFilterClick={onFilterClick}
										selectedFilter={
											location?.pathname?.includes('/dashboard/guided-insights')
												? STORE
												: selectedFilter
										}
									/>
								</Box>

								<Box className={classesName.appFilter}>
									<HierarchyFilter />
								</Box>
								{!location?.pathname?.includes(
									'/dashboard/guided-insights'
								) && (
									<Box className={classesName.appFilter}>
										<CopyPasteUpcs />
									</Box>
								)}
							</>
						)}
					</Box>
					{!location?.pathname?.includes('/dashboard/guided-insights') &&
						!isStoreDrilldown && (
							<Box className={classesName.menuFiltersRight}>
								<DataRefresh weekFilter={weekFilter} />
							</Box>
						)}
				</Box>
			</Box>
		</>
	);
}

Filter.propTypes = {
	setWeek: PropTypes.func,
	setWeekFilter: PropTypes.func,
	weekFilter: PropTypes.number,
	setSelectedFilter: PropTypes.func,
	selectedFilter: PropTypes.string,
	startWeek: PropTypes.number,
	endWeek: PropTypes.number,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	tabName: PropTypes.string,
	isItemDrilldown: PropTypes.bool,
	isStoreDrilldown: PropTypes.bool,
	setCurrentFilters: PropTypes.func,
};

export default Filter;
